import { ActionContext, ActionTree, CommitOptions } from 'vuex'
import { PaletteStorageName, State, Tools, TrinketGridStorageName } from '@/store/state'
import { Mutations } from '@/store/mutations'
import { Point } from '@/domain/models/Point'
import { ToastOptions } from '@/components/Common/ToastOptions'
import { TrinketPoint } from '@/domain/models/TrinketPoint'
import { TrinketText } from '@/domain/models/TrinketText'
import { AutoSaveConfiguration, GridConfiguration } from '@/domain/models/TrinketSettings'
import { isValidColor, migrateFromV1 } from '@/domain/Utils'

type ActionArguments = Omit<ActionContext<State, State>, 'commit'> & {
    commit<K extends keyof Mutations> (
        key: K,
        payload?: Parameters<Mutations[K]>[1],
        options?: CommitOptions
    ): ReturnType<Mutations[K]>
}

export interface Actions {
    addLine (state: ActionArguments, point: Point): void

    addPoint (state: ActionArguments, point: Point): void

    addText (state: ActionArguments, point: Point): void

    cancelCurrentLine (state: ActionArguments): void

    closeSettings (state: ActionArguments): void

    createToast (state: ActionArguments, options: ToastOptions): void

    exportPalette (state: ActionArguments): void

    exportTrinket (state: ActionArguments): void

    loadPalette (state: ActionArguments, palette: string): void

    loadTrinket (state: ActionArguments, trinket: string): void

    newTrinket (state: ActionArguments): void

    pickColor (state: ActionArguments, point: TrinketPoint): void

    removeLine (state: ActionArguments, point: TrinketPoint): void

    removePoint (state: ActionArguments, point: Point): void

    removeText (state: ActionArguments, text: TrinketText): void

    openSettings (state: ActionArguments): void

    saveTrinket (state: ActionArguments): void

    selectTool (state: ActionArguments, tool: Tools): void

    updateColor (state: ActionArguments, payload: { color: string; zone: 'fill' | 'stroke' | 'lineStroke' }): void

    updateGridConfiguration (state: ActionArguments, payload: Partial<GridConfiguration>): void

    updateAutoSaveConfiguration (state: ActionArguments, payload: Partial<AutoSaveConfiguration>): void

    updateSaveOnExitConfiguration ({ commit }: ActionArguments, saveOnExit: boolean): void

    updateContinueLineConfiguration ({ commit }: ActionArguments, continueLine: boolean): void

    updateLine (state: ActionArguments, point: Point): void

    updateText (state: ActionArguments, text: TrinketText): void
}

export const actions: ActionTree<State, State> & Actions = {
    addLine ({ commit, state }: ActionArguments, point: Point): void {
        commit('trinketAddLine', point)
        commit('paletteAddColor', state.trinketGrid.colors.lineStroke)
    },
    addPoint ({ commit, state }: ActionArguments, point: Point): void {
        commit('trinketAddPoint', point)

        let color = ''

        switch (state.trinketGrid.selectedTool) {
            case Tools.FILLER:
                color = state.trinketGrid.colors.fill
                break
            case Tools.BORDER:
                color = state.trinketGrid.colors.stroke
                break
        }

        commit('paletteAddColor', color)
    },
    addText ({ commit }: ActionArguments, point: Point): void {
        commit('trinketAddText', point)
    },
    cancelCurrentLine ({ commit }: ActionArguments): void {
        commit('trinketCancelLine')
    },
    closeSettings ({ commit }: ActionArguments): void {
        commit('closeSettings')
    },
    createToast ({ commit }: ActionArguments, options: ToastOptions): void {
        commit('createToast', options)
    },
    exportPalette ({
        commit,
        state
    }: ActionArguments): void {
        commit('saveToFile', {
            fileName: 'palette.json',
            type: 'application/json',
            data: JSON.stringify(state.palette)
        })
    },
    exportTrinket ({
        commit,
        state
    }: ActionArguments): void {
        commit('saveToFile', {
            fileName: 'trinketGrid.json',
            type: 'application/json',
            data: JSON.stringify(state)
        })
    },
    loadPalette ({
        commit,
        dispatch
    }: ActionArguments, palette: string): void {
        const parsedPalette = JSON.parse(palette)

        if (Array.isArray(parsedPalette) && parsedPalette.every(isValidColor)) {
            commit('loadPalette', parsedPalette)
        } else {
            dispatch('createToast', {
                data: 'Error al cargar la paleta. Comprueba que el archivo es correcto',
                theme: 'error'
            })
        }
    },
    loadTrinket ({
        commit,
        dispatch
    }: ActionArguments, trinket: string): void {
        let parsedTrinket = JSON.parse(trinket)
        // V1
        if (!('version' in parsedTrinket)) {
            parsedTrinket = migrateFromV1(parsedTrinket)

            if (!parsedTrinket) {
                dispatch('createToast', {
                    data: 'Error al migrar los datos. Comprueba que el archivo es correcto',
                    theme: 'error'
                })
                return
            }
        } else {
            if (parsedTrinket.version !== process.env.PACKAGE_VERSION) {
                // Old version of saved file
                // Migrate

                if (parsedTrinket.version === '2.0.0') {
                    parsedTrinket.configuration.grid.showGridAxis = true
                    parsedTrinket.configuration.grid.gridAxisColor = 'hsl(0, 79%, 54%)'
                }
            }
        }

        commit('trinketLoad', parsedTrinket)
    },
    newTrinket ({
        state,
        commit
    }: ActionArguments): void {
        const points = state.trinketGrid.points
        const lines = state.trinketGrid.lines
        const texts = state.trinketGrid.texts

        if (points.length > 0 || lines.length > 0 || texts.length > 0) {
            if (confirm('¿Seguro que quieres crear un nuevo dibujo?')) {
                commit('newTrinket')
            }
        } else {
            commit('newTrinket')
        }
    },
    openSettings ({ commit }: ActionArguments): void {
        commit('openSettings')
    },
    pickColor ({ commit }: ActionArguments, point: TrinketPoint): void {
        commit('trinketPickColor', point)
    },
    removeLine ({ commit }: ActionArguments, point: TrinketPoint): void {
        commit('trinketRemoveLine', point)
    },
    removePoint ({ commit }: ActionArguments, point: Point): void {
        commit('trinketRemovePoint', point)
    },
    removeText ({ commit }: ActionArguments, text: TrinketText): void {
        commit('trinketRemoveText', text)
    },
    saveTrinket ({
        state,
        commit,
        dispatch
    }: ActionArguments): void {
        commit('saveToLocalStorage', {
            key: TrinketGridStorageName,
            data: JSON.stringify(state)
        })

        dispatch('createToast', { data: '¡Dibujo guardado!' })
    },
    selectTool ({ commit }: ActionArguments, tool: Tools): void {
        commit('trinketSelectTool', tool)
    },
    updateColor ({ commit }: ActionArguments, {
        color,
        zone
    }: { color: string; zone: 'fill' | 'stroke' | 'lineStroke' }): void {
        commit('trinketSetColor', {
            color,
            zone
        })
    },
    updateAutoSaveConfiguration ({
        state,
        commit,
        dispatch
    }: ActionArguments, payload: Partial<AutoSaveConfiguration>): void {
        const active = payload.active ?? state.configuration.saving.autoSave.active
        const timeInterval = payload.timeInterval ?? state.configuration.saving.autoSave.timeInterval

        if (active) {
            if (state.saveInterval) {
                clearInterval(state.saveInterval)
            }

            const interval = setInterval(() => {
                dispatch('saveTrinket')
                commit('saveToLocalStorage', {
                    key: PaletteStorageName,
                    data: JSON.stringify(state.palette)
                })
            }, timeInterval)

            commit('configurationUpdateAutoSave', {
                active,
                timeInterval,
                interval
            })
        } else {
            if (state.saveInterval) {
                clearInterval(state.saveInterval)
            }

            commit('removeAutoSave')
        }
    },
    updateGridConfiguration ({ commit }: ActionArguments, payload: Partial<GridConfiguration>): void {
        commit('configurationUpdateGridConfiguration', payload)
    },
    updateSaveOnExitConfiguration ({ commit, state, dispatch }: ActionArguments, saveOnExit: boolean): void {
        const handleBeforeUnload = () => {
            commit('saveToLocalStorage', {
                key: PaletteStorageName,
                data: JSON.stringify(state.palette)
            })
            dispatch('saveTrinket')
        }

        if (saveOnExit) {
            window.addEventListener('beforeunload', handleBeforeUnload, false)
        } else {
            window.removeEventListener('beforeunload', handleBeforeUnload)
        }

        commit('configurationUpdateSaveOnExit', saveOnExit)
    },
    updateContinueLineConfiguration ({ commit }: ActionArguments, continueLine: boolean): void {
        commit('configurationUpdateContinueLine', continueLine)
    },
    updateLine ({ commit }: ActionArguments, point: Point): void {
        commit('trinketUpdateLine', point)
    },
    updateText ({ commit }: ActionArguments, text: TrinketText): void {
        commit('trinketUpdateText', text)
    }
}
